import type { AnnotationRecordResponse } from "@/hooks/useLabelingQueries";
import { candidateVerdictKey } from "@/hooks/useCandidateVerdicts";
import type { SegmentationPreview } from "@/predictions/segmentationPreviews";

/** Join saved card data to predictions without changing their geometry/edit identity. */
export function getSegmentationCardAnnotations({
  annotations,
  previews,
  linkedAnnotationIds = {},
  projectId,
  studyUid,
}: {
  annotations: readonly AnnotationRecordResponse[];
  previews: readonly Pick<SegmentationPreview, "id" | "condition" | "sourceSegmentIndex">[];
  linkedAnnotationIds?: Readonly<Record<string, string | null>>;
  projectId: string;
  studyUid: string;
}): {
  additional: AnnotationRecordResponse[];
  byPreviewId: Partial<Record<string, AnnotationRecordResponse>>;
} {
  const cleanStudyUid = studyUid.split("::")[0];
  // Only display records belonging to the current case and project.
  const scoped = annotations.filter(
    (annotation) =>
      annotation.project_id === projectId && annotation.study_instance_uid === cleanStudyUid,
  );
  const byId = new Map(scoped.map((annotation) => [annotation.annotation_id, annotation]));
  const byCandidate = new Map<string, AnnotationRecordResponse>();
  for (const annotation of scoped) {
    const condition = annotation.attributes.ai_prediction_condition;
    const source = annotation.attributes.ai_prediction_source_segment_index;
    if (typeof condition === "string" && typeof source === "number") {
      byCandidate.set(candidateVerdictKey(condition, source), annotation);
    }
  }
  const byPreviewId: Partial<Record<string, AnnotationRecordResponse>> = {};
  const representedIds = new Set<string>();
  for (const preview of previews) {
    const key = candidateVerdictKey(preview.condition, preview.sourceSegmentIndex);
    const linkedId = linkedAnnotationIds[key];
    const annotation = linkedId ? byId.get(linkedId) : byCandidate.get(key);
    if (!annotation) continue;
    byPreviewId[preview.id] = annotation;
    representedIds.add(annotation.annotation_id);
  }
  return {
    additional: scoped.filter((annotation) => !representedIds.has(annotation.annotation_id)),
    byPreviewId,
  };
}
